import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { useRouter } from 'next/router';
import { TaskMimeType, TaskStatus, TaskType } from '@/services/uploaderService';
import { ImageSelectorMedia } from '@/services/editorService';
import {
  bulkMediaListState,
  downloadMediaState,
  inputMediaNamesState,
  mediaShownInComparerState,
} from '@/stores/mediaStore';
import {
  useMediaService,
  useUploaderService,
} from '@/layout/appWrapper/ServiceProvider';
import { isReprocessingMediaState } from '@/stores/isReprocessingMediaStore';
import useUploadHandler from '@/features/uploadMedia/useUploadHandler';
import { editorLoadingState } from '@/stores/editorStore';
import { useTracker } from '@/core/tracking';

export default function useImageSelectorToolbar(
  mediaMaxLength: number,
  setProgressValue: Dispatch<SetStateAction<number>>
) {
  const { track } = useTracker();
  const router = useRouter();
  const { taskId } = router.query;

  const [mediasToDownload, setMediasToDownload] =
    useRecoilState(downloadMediaState);
  const setMediaInComparer = useSetRecoilState(mediaShownInComparerState);
  const [mediaName, setMediaName] = useRecoilState(inputMediaNamesState);

  const { deleteTasks } = useUploaderService();
  const setIsReprocessingMedia = useSetRecoilState(isReprocessingMediaState);
  const { useMedia } = useMediaService();
  const { getMediaAndUpdateStore, getBulkUploadWithPolling } = useMedia();
  const { reprocess } = useUploadHandler('result');
  const setIsLoadingEditor = useSetRecoilState(editorLoadingState);
  const completeMediaList = useRecoilValue(bulkMediaListState);

  const calcProgressBarPercentage = (currentValue: number, maxValue: number) =>
    (currentValue * 100) / maxValue;

  useEffect(() => {
    const percentage = calcProgressBarPercentage(
      completeMediaList.length,
      mediaMaxLength
    );
    setProgressValue(percentage);
  }, [completeMediaList.length, mediaMaxLength, setProgressValue]);

  const isSelectionModeEnabled = useMemo(
    () => mediasToDownload.length > 1,
    [mediasToDownload.length]
  );

  const addMediaToSelectedList = useCallback(
    (mediaToAdd: ImageSelectorMedia) => {
      if (
        mediasToDownload
          .map((selectedMedia) => selectedMedia.id)
          .includes(mediaToAdd.id)
      ) {
        return;
      }

      setMediasToDownload((prev) => [...prev, mediaToAdd]);
    },
    [mediasToDownload, setMediasToDownload]
  );

  const removeMediaFromSelectedList = useCallback(
    (mediaToRemove: ImageSelectorMedia['id'][]) => {
      const newMediasToDownload = mediasToDownload.filter(
        (selectedMedia) => !mediaToRemove.includes(selectedMedia.id)
      );

      setMediasToDownload(newMediasToDownload);
    },
    [mediasToDownload, setMediasToDownload]
  );

  const onToggleMedia = useCallback(
    ({
      toggledMedia,
      checked,
    }: {
      toggledMedia: ImageSelectorMedia;
      checked: boolean;
    }) => {
      if (checked) addMediaToSelectedList(toggledMedia);

      if (!checked && isSelectionModeEnabled) {
        removeMediaFromSelectedList([toggledMedia.id]);
      }
    },
    [
      addMediaToSelectedList,
      isSelectionModeEnabled,
      removeMediaFromSelectedList,
    ]
  );

  const onChangeMediaShownInComparer = useCallback(
    (mediaToShow: ImageSelectorMedia) => {
      setMediaInComparer(mediaToShow);

      if (!isSelectionModeEnabled) {
        setMediasToDownload([mediaToShow]);
      }
    },
    [isSelectionModeEnabled, setMediaInComparer, setMediasToDownload]
  );

  const onBatchDeleteMedia = useCallback(async () => {
    if (completeMediaList.length === 1) {
      return;
    }

    const selectedMediasIndexes = mediasToDownload.map((m) => m.bulkPosition!);
    const mediaNamesToKeep = mediaName.filter(
      (name, index) => !selectedMediasIndexes.includes(index)
    );
    setMediaName(mediaNamesToKeep);

    const mediaIdsToDelete: string[] = mediasToDownload.map((m) => m.id);
    await deleteTasks(mediaIdsToDelete);
    removeMediaFromSelectedList(mediaIdsToDelete);
    await getMediaAndUpdateStore();
  }, [
    completeMediaList.length,
    deleteTasks,
    getMediaAndUpdateStore,
    mediaName,
    mediasToDownload,
    removeMediaFromSelectedList,
    setMediaName,
  ]);

  const onDeleteMedia = useCallback(
    async (mediaToDelete: ImageSelectorMedia) => {
      if (completeMediaList.length === 1) {
        return;
      }

      setMediaName([
        ...mediaName.slice(0, mediaToDelete.bulkPosition!),
        ...mediaName.slice(mediaToDelete.bulkPosition! + 1),
      ]);

      const mediaIdToDelete: string[] = [mediaToDelete.id];
      await deleteTasks(mediaIdToDelete);
      removeMediaFromSelectedList(mediaIdToDelete);

      await getMediaAndUpdateStore();
    },
    [
      completeMediaList.length,
      deleteTasks,
      getMediaAndUpdateStore,
      mediaName,
      removeMediaFromSelectedList,
      setMediaName,
    ]
  );

  const toggleAllMedia = useCallback(
    ({ shouldSelectAll }: { shouldSelectAll: boolean }) => {
      const mediaList = shouldSelectAll ? completeMediaList : [];
      setMediasToDownload(mediaList);
    },
    [completeMediaList, setMediasToDownload]
  );

  const onReprocessFailedMedia = useCallback(
    async (mediaToReprocess: ImageSelectorMedia) => {
      const reprocessedMedia = await reprocess({
        currentBulkUploadId: taskId as string,
        bulkUploadReprocessPayload: {
          inputReprocessList: [
            {
              aiPipeline: mediaToReprocess.aiPipeline!,
              clientTaskId: mediaToReprocess.id,
              outputContentType: mediaToReprocess.contentType as TaskMimeType,
            },
          ],
        },
      });
      const taskListCompleted = reprocessedMedia.taskList?.every(
        (t) =>
          t.status === TaskStatus.Completed || t.status === TaskStatus.Exported
      );
      if (!taskListCompleted) {
        getBulkUploadWithPolling({ isBulkProcessingCompleted: false });
      } else {
        setIsReprocessingMedia(false);
        setIsLoadingEditor(false);
        track(TaskStatus.Reprocessing, TaskStatus.Completed, {
          actionValue: 'browse',
          mediaType: TaskType.Image,
          bulkUploadId: reprocessedMedia.bulkUploadId,
          mediaQuantity: reprocessedMedia.taskList?.length,
          aiPipelines: reprocessedMedia.taskList?.map(
            (t) => t.result.outputs[0].aiPipeline
          ),
        });
      }
    },
    [
      getBulkUploadWithPolling,
      reprocess,
      setIsLoadingEditor,
      setIsReprocessingMedia,
      taskId,
      track,
    ]
  );

  return {
    completeMediaList,
    isSelectionModeEnabled,
    mediasToDownload,
    onChangeMediaShownInComparer,
    onBatchDeleteMedia,
    onDeleteMedia,
    onReprocessFailedMedia,
    onToggleMedia,
    toggleAllMedia,
    track,
  };
}
